/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * SessionRefresher Component
 * ============================================================================
 *
 * Refreshes the stored user from GET /auth/me whenever the app becomes
 * authenticated (login) or is loaded/reloaded with an existing session.
 *
 * Why this is needed: the login/MFA response is built on an unauthenticated
 * request, so the backend cannot resolve `currentDayRestricted` there and the
 * value defaults to false. A page reload also rehydrates a possibly-stale user
 * from localStorage. Fetching /auth/me (an authenticated request) returns the
 * correct day-scope flag and keeps the profile in sync with the server.
 *
 * Render once, high in the tree (e.g. in App.tsx next to CurrencyInitializer).
 *
 * ============================================================================
 */

import { useEffect } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { getCurrentUser } from '@/services/auth.service';

/**
 * Component that refreshes the authenticated user from /auth/me. Renders nothing.
 */
export function SessionRefresher(): null {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const updateUser = useAuthStore((s) => s.updateUser);

  useEffect(() => {
    if (!isAuthenticated) return;

    let cancelled = false;
    getCurrentUser()
      .then((user) => {
        if (!cancelled) updateUser(user);
      })
      .catch(() => {
        // Non-fatal: keep the persisted user. A 401 is handled by the API
        // client's refresh/logout interceptor, not here.
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, updateUser]);

  return null;
}

export default SessionRefresher;
